import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Zap, ArrowLeft, MailCheck } from "lucide-react";

const PRODUCT_NAME = "Production Pay Tracker";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSubmitted(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-sky-50 p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-sky-500 to-cyan-500 flex items-center justify-center">
            <Zap className="h-6 w-6 text-white" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500">Smarter Pay. Stronger Teams.</p>
            <h1 className="text-xl font-bold font-display tracking-tight text-slate-900">{PRODUCT_NAME}</h1>
          </div>
        </div>

        {!submitted ? (
          <>
            <h2 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900 mb-2">Forgot password?</h2>
            <p className="text-slate-500 mb-8">
              Enter your email and we'll send you a link to set a new one.
            </p>

            <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-password-form">
              <div>
                <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-700">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@yourcompany.com"
                  className="h-12 mt-1.5"
                  data-testid="forgot-password-email-input"
                />
              </div>

              {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" data-testid="forgot-password-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 text-base font-bold bg-sky-600 hover:bg-sky-700"
                data-testid="forgot-password-submit-btn"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send reset link"}
              </Button>

              <p className="text-center text-sm text-slate-600 pt-2">
                <Link to="/login" className="font-semibold text-sky-700 hover:text-sky-800 inline-flex items-center gap-1" data-testid="forgot-password-back-link">
                  <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
                </Link>
              </p>
            </form>
          </>
        ) : (
          <div className="text-center" data-testid="forgot-password-sent">
            <div className="h-16 w-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <MailCheck className="h-8 w-8 text-emerald-700" />
            </div>
            <h2 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900 mb-2">Check your email</h2>
            <p className="text-slate-600 mb-2">
              If an account exists for <strong>{email}</strong>, we just sent a reset link.
            </p>
            <p className="text-sm text-slate-500 mb-8">
              The link expires in 1 hour. Didn't get it? Check spam, or try again.
            </p>
            <Link to="/login">
              <Button variant="outline" className="w-full h-12" data-testid="forgot-password-back-to-login-btn">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to sign in
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
